import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  TrendingUp as TrendingUpIcon,
  AttachMoney as MoneyIcon,
  Assessment as AssessmentIcon,
  CheckCircle as ApprovedIcon,
  Cancel as RejectedIcon,
  PendingActions as PendingIcon,
  Business as BusinessIcon,
  ExpandMore as ExpandMoreIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { useAppSelector } from '../../store';
import { API_CONFIG } from '../../config/api';
import { PurchaseRequest, RequestStatus, RequestFrequency } from '../../types/request.types';

interface OrganizationStats {
  totalRequests: number;
  approvedRequests: number;
  rejectedRequests: number;
  pendingRequests: number;
  totalAmount: number;
  approvedAmount: number;
  averageAmount: number;
}

interface DepartmentStats {
  departmentId: string;
  departmentName: string;
  totalRequests: number;
  approvedRequests: number;
  rejectedRequests: number;
  pendingRequests: number;
  totalAmount: number;
  approvedAmount: number;
  averageAmount: number;
}

interface Department {
  id: string;
  name: string;
}

interface Filters {
  departmentId: string;
  status: string;
  frequency: string;
  renewalMonth: string;
  fromDate: string;
  toDate: string;
  minAmount: string;
  maxAmount: string;
  searchTerm: string;
}

const GeneralManagerReports: React.FC = () => {
  const navigate = useNavigate();
  const user = useAppSelector(state => state.auth.user);
  const [requests, setRequests] = useState<PurchaseRequest[]>([]);
  const [filteredRequests, setFilteredRequests] = useState<PurchaseRequest[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [organizationStats, setOrganizationStats] = useState<OrganizationStats | null>(null);
  const [departmentStats, setDepartmentStats] = useState<DepartmentStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  
  const [filters, setFilters] = useState<Filters>({
    departmentId: '',
    status: '',
    frequency: '',
    renewalMonth: '',
    fromDate: '',
    toDate: '',
    minAmount: '',
    maxAmount: '',
    searchTerm: '',
  });

  useEffect(() => {
    fetchOrganizationData();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [requests, filters]);

  const fetchOrganizationData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch all requests
      const requestsResponse = await fetch(`${API_CONFIG.BASE_URL}/requests`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!requestsResponse.ok) {
        throw new Error('Failed to fetch requests');
      }

      const allRequests: PurchaseRequest[] = await requestsResponse.json();

      // Fetch departments
      const departmentsResponse = await fetch(`${API_CONFIG.BASE_URL}/users/departments`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (departmentsResponse.ok) {
        const departmentData = await departmentsResponse.json();
        setDepartments(departmentData); // Already has {id, name} structure
      }

      setRequests(allRequests);

      // Calculate organization-wide statistics (will use filtered data later)
      calculateOrganizationStats(allRequests);
      
      // Calculate department-wise statistics
      calculateDepartmentStats(allRequests);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load organization data');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    // Exclude DRAFT requests from the organization report entirely
    let filtered = requests.filter(r => r.status !== RequestStatus.DRAFT);

    // Department filter
    if (filters.departmentId) {
      filtered = filtered.filter(request => request.departmentId === filters.departmentId);
    }

    // Status filter
    if (filters.status) {
      filtered = filtered.filter(request => request.status === filters.status);
    }

    // Frequency filter
    if (filters.frequency) {
      filtered = filtered.filter(request => request.frequency === filters.frequency);
    }

    // Renewals in Month filter (for subscription tracking)
    if (filters.renewalMonth) {
      filtered = filtered.filter(request => {
        // Only include recurring subscriptions (MONTHLY, YEARLY)
        const isRecurring = request.frequency === RequestFrequency.MONTHLY || 
                           request.frequency === RequestFrequency.YEARLY;
        
        if (!isRecurring) {
          return false;
        }
        
        const selectedMonth = parseInt(filters.renewalMonth); // 0-11
        
        // For MONTHLY subscriptions: Include ALL monthly subscriptions 
        // because they renew every month regardless of original delivery date
        if (request.frequency === RequestFrequency.MONTHLY) {
          return true;
        }
        
        // For YEARLY subscriptions: Only include those with delivery month matching selected month
        // because they renew only once per year in that specific month
        if (request.frequency === RequestFrequency.YEARLY) {
          const deliveryDate = new Date(request.requestedDeliveryDate);
          const deliveryMonth = deliveryDate.getMonth(); // 0-11
          return deliveryMonth === selectedMonth;
        }
        
        return false;
      });
    }

    // Date range filter (by delivery date)
    if (filters.fromDate) {
      filtered = filtered.filter(request => 
        new Date(request.requestedDeliveryDate) >= new Date(filters.fromDate)
      );
    }

    if (filters.toDate) {
      filtered = filtered.filter(request => 
        new Date(request.requestedDeliveryDate) <= new Date(filters.toDate + 'T23:59:59')
      );
    }

    // Amount range filter
    if (filters.minAmount) {
      filtered = filtered.filter(request => request.amount >= parseFloat(filters.minAmount));
    }

    if (filters.maxAmount) {
      filtered = filtered.filter(request => request.amount <= parseFloat(filters.maxAmount));
    }

    // Search term filter (description, title, requester)
    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase();
      filtered = filtered.filter(request => 
        request.description.toLowerCase().includes(searchLower) ||
        request.title.toLowerCase().includes(searchLower) ||
        request.requester?.firstName.toLowerCase().includes(searchLower) ||
        request.requester?.lastName.toLowerCase().includes(searchLower)
      );
    }

    setFilteredRequests(filtered);
    
    // Recalculate stats with filtered data
    calculateOrganizationStats(filtered);
    calculateDepartmentStats(filtered);
  };

  const handleFilterChange = (field: keyof Filters, value: string) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const clearFilters = () => {
    setFilters({
      departmentId: '',
      status: '',
      frequency: '',
      renewalMonth: '',
      fromDate: '',
      toDate: '',
      minAmount: '',
      maxAmount: '',
      searchTerm: '',
    });
  };

  const getActiveFiltersCount = () => {
    return Object.values(filters).filter(value => value !== '').length;
  };

  const calculateOrganizationStats = (allRequests: PurchaseRequest[]) => {
    // Exclude DRAFT requests from all calculations
    const submittedRequests = allRequests.filter(r => r.status !== RequestStatus.DRAFT);
    
    const approved = submittedRequests.filter(r => 
      r.status === RequestStatus.APPROVED || 
      r.status === RequestStatus.PAID || 
      r.status === RequestStatus.CLOSED
    );
    const rejected = submittedRequests.filter(r => r.status === RequestStatus.REJECTED);
    const pending = submittedRequests.filter(r => 
      r.status === RequestStatus.PENDING_MANAGER || 
      r.status === RequestStatus.PENDING_GENERAL_MANAGER ||
      r.status === RequestStatus.PENDING_SENIOR_MANAGER ||
      r.status === RequestStatus.PENDING_FINANCE ||
      r.status === RequestStatus.PENDING_CFO ||
      r.status === RequestStatus.MORE_INFO_NEEDED
    );

    const totalAmount = submittedRequests.reduce((sum, r) => sum + r.amount, 0);
    const approvedAmount = approved.reduce((sum, r) => sum + r.amount, 0);

    setOrganizationStats({
      totalRequests: submittedRequests.length,
      approvedRequests: approved.length,
      rejectedRequests: rejected.length,
      pendingRequests: pending.length,
      totalAmount,
      approvedAmount,
      averageAmount: submittedRequests.length > 0 ? totalAmount / submittedRequests.length : 0,
    });
  };

  const calculateDepartmentStats = (allRequests: PurchaseRequest[]) => {
    // Group requests by department ID (excluding DRAFT requests)
    const submittedRequests = allRequests.filter(r => r.status !== RequestStatus.DRAFT);
    const departmentGroups = submittedRequests.reduce((groups: { [key: string]: PurchaseRequest[] }, request) => {
      const deptId = request.departmentId;
      if (!groups[deptId]) {
        groups[deptId] = [];
      }
      groups[deptId].push(request);
      return groups;
    }, {});

    // Calculate stats for each department
    const deptStats = Object.entries(departmentGroups).map(([departmentId, deptRequests]) => {
      // Get department name from the first request (all requests in group have same department)
      const departmentName = deptRequests[0]?.department?.name || 'Unknown Department';
      
      const approved = deptRequests.filter(r => 
        r.status === RequestStatus.APPROVED || 
        r.status === RequestStatus.PAID || 
        r.status === RequestStatus.CLOSED
      );
      const rejected = deptRequests.filter(r => r.status === RequestStatus.REJECTED);
      const pending = deptRequests.filter(r => 
        r.status === RequestStatus.PENDING_MANAGER || 
        r.status === RequestStatus.PENDING_GENERAL_MANAGER ||
        r.status === RequestStatus.PENDING_SENIOR_MANAGER ||
        r.status === RequestStatus.PENDING_FINANCE ||
        r.status === RequestStatus.PENDING_CFO ||
        r.status === RequestStatus.MORE_INFO_NEEDED
      );

      const totalAmount = deptRequests.reduce((sum, r) => sum + r.amount, 0);
      const approvedAmount = approved.reduce((sum, r) => sum + r.amount, 0);

      return {
        departmentId,
        departmentName,
        totalRequests: deptRequests.length,
        approvedRequests: approved.length,
        rejectedRequests: rejected.length,
        pendingRequests: pending.length,
        totalAmount,
        approvedAmount,
        averageAmount: deptRequests.length > 0 ? totalAmount / deptRequests.length : 0,
      };
    });

    // Sort by total amount descending
    deptStats.sort((a, b) => b.totalAmount - a.totalAmount);
    setDepartmentStats(deptStats);
  };

  const getStatusColor = (status: RequestStatus) => {
    switch (status) {
      case RequestStatus.APPROVED:
      case RequestStatus.PAID:
      case RequestStatus.CLOSED:
        return 'success';
      case RequestStatus.REJECTED:
        return 'error';
      case RequestStatus.PENDING_MANAGER:
      case RequestStatus.PENDING_GENERAL_MANAGER:
      case RequestStatus.PENDING_SENIOR_MANAGER:
      case RequestStatus.PENDING_FINANCE:
      case RequestStatus.PENDING_CFO:
        return 'warning';
      case RequestStatus.MORE_INFO_NEEDED:
        return 'info';
      default:
        return 'default';
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/dashboard')}
        >
          Back to Dashboard
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box display="flex" alignItems="center" gap={2} mb={4}>
        <IconButton 
          onClick={() => navigate('/dashboard')}
          sx={{ 
            backgroundColor: 'primary.main', 
            color: 'white',
            '&:hover': {
              backgroundColor: 'primary.dark',
            }
          }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" component="h1">
          Organization Reports
        </Typography>
        <BusinessIcon sx={{ fontSize: 40, color: 'primary.main' }} />
      </Box>

      {/* Advanced Filters */}
      <Accordion 
        expanded={filtersExpanded} 
        onChange={() => setFiltersExpanded(!filtersExpanded)}
        sx={{ mb: 3 }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="filters-content"
          id="filters-header"
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterIcon />
            <Typography variant="h6">
              Advanced Filters
            </Typography>
            {getActiveFiltersCount() > 0 && (
              <Chip 
                label={`${getActiveFiltersCount()} active`} 
                size="small" 
                color="primary" 
              />
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            {/* Search Term */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Search"
                placeholder="Search by description, requester name..."
                value={filters.searchTerm}
                onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
                variant="outlined"
              />
            </Grid>

            {/* Department Filter */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Department</InputLabel>
                <Select
                  value={filters.departmentId}
                  label="Department"
                  onChange={(e) => handleFilterChange('departmentId', e.target.value)}
                >
                  <MenuItem value="">All Departments</MenuItem>
                  {departments.map((dept) => (
                    <MenuItem key={dept.id} value={dept.id}>
                      {dept.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Status Filter */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status}
                  label="Status"
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                >
                  <MenuItem value="">All Statuses</MenuItem>
                  <MenuItem value={RequestStatus.DRAFT}>Draft</MenuItem>
                  <MenuItem value={RequestStatus.PENDING_MANAGER}>Pending Manager</MenuItem>
                  <MenuItem value={RequestStatus.PENDING_SENIOR_MANAGER}>Pending Senior Manager</MenuItem>
                  <MenuItem value={RequestStatus.PENDING_GENERAL_MANAGER}>Pending General Manager</MenuItem>
                  <MenuItem value={RequestStatus.PENDING_CFO}>Pending CFO</MenuItem>
                  <MenuItem value={RequestStatus.PENDING_FINANCE}>Pending Finance</MenuItem>
                  <MenuItem value={RequestStatus.APPROVED}>Approved</MenuItem>
                  <MenuItem value={RequestStatus.REJECTED}>Rejected</MenuItem>
                  <MenuItem value={RequestStatus.PAID}>Paid</MenuItem>
                  <MenuItem value={RequestStatus.CLOSED}>Closed</MenuItem>
                  <MenuItem value={RequestStatus.MORE_INFO_NEEDED}>More Info Needed</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Frequency Filter */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Frequency</InputLabel>
                <Select
                  value={filters.frequency}
                  label="Frequency"
                  onChange={(e) => handleFilterChange('frequency', e.target.value)}
                >
                  <MenuItem value="">All Frequencies</MenuItem>
                  <MenuItem value={RequestFrequency.ONCE}>Once</MenuItem>
                  <MenuItem value={RequestFrequency.MONTHLY}>Monthly</MenuItem>
                  <MenuItem value={RequestFrequency.YEARLY}>Yearly</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Renewals in Month Filter */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Renewals by Delivery Month</InputLabel>
                <Select
                  value={filters.renewalMonth}
                  label="Renewals by Delivery Month"
                  onChange={(e) => handleFilterChange('renewalMonth', e.target.value)}
                >
                  <MenuItem value="">All Months</MenuItem>
                  <MenuItem value="0">January</MenuItem>
                  <MenuItem value="1">February</MenuItem>
                  <MenuItem value="2">March</MenuItem>
                  <MenuItem value="3">April</MenuItem>
                  <MenuItem value="4">May</MenuItem>
                  <MenuItem value="5">June</MenuItem>
                  <MenuItem value="6">July</MenuItem>
                  <MenuItem value="7">August</MenuItem>
                  <MenuItem value="8">September</MenuItem>
                  <MenuItem value="9">October</MenuItem>
                  <MenuItem value="10">November</MenuItem>
                  <MenuItem value="11">December</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Delivery Date Range */}
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Delivery From Date"
                type="date"
                value={filters.fromDate}
                onChange={(e) => handleFilterChange('fromDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Delivery To Date"
                type="date"
                value={filters.toDate}
                onChange={(e) => handleFilterChange('toDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* Amount Range */}
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Min Amount ($)"
                type="number"
                value={filters.minAmount}
                onChange={(e) => handleFilterChange('minAmount', e.target.value)}
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Max Amount ($)"
                type="number"
                value={filters.maxAmount}
                onChange={(e) => handleFilterChange('maxAmount', e.target.value)}
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>

            {/* Filter Actions */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  startIcon={<ClearIcon />}
                  onClick={clearFilters}
                  disabled={getActiveFiltersCount() === 0}
                >
                  Clear Filters
                </Button>
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                  Showing {filteredRequests.length} of {requests.length} requests
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Organization-wide Statistics */}
      {organizationStats && (
        <Paper sx={{ p: 3, mb: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AssessmentIcon color="primary" />
            Organization Overview
          </Typography>
          
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={3}>
              <Card sx={{ bgcolor: 'primary.50' }}>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={2}>
                    <TrendingUpIcon color="primary" />
                    <Box>
                      <Typography variant="h4" color="primary.main">
                        {organizationStats.totalRequests}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total Requests
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={3}>
              <Card sx={{ bgcolor: 'success.50' }}>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={2}>
                    <ApprovedIcon color="success" />
                    <Box>
                      <Typography variant="h4" color="success.main">
                        {organizationStats.approvedRequests}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Approved Requests
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={3}>
              <Card sx={{ bgcolor: 'warning.50' }}>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={2}>
                    <PendingIcon color="warning" />
                    <Box>
                      <Typography variant="h4" color="warning.main">
                        {organizationStats.pendingRequests}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Pending Requests
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={3}>
              <Card sx={{ bgcolor: 'error.50' }}>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={2}>
                    <RejectedIcon color="error" />
                    <Box>
                      <Typography variant="h4" color="error.main">
                        {organizationStats.rejectedRequests}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Rejected Requests
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Total Amount
                  </Typography>
                  <Typography variant="h4" color="primary.main">
                    {formatCurrency(organizationStats.totalAmount)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Approved Amount
                  </Typography>
                  <Typography variant="h4" color="success.main">
                    {formatCurrency(organizationStats.approvedAmount)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Average Request
                  </Typography>
                  <Typography variant="h4" color="info.main">
                    {formatCurrency(organizationStats.averageAmount)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Department Breakdown */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BusinessIcon color="primary" />
          Department Breakdown
        </Typography>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Department</strong></TableCell>
                <TableCell align="center"><strong>Total Requests</strong></TableCell>
                <TableCell align="center"><strong>Approved</strong></TableCell>
                <TableCell align="center"><strong>Pending</strong></TableCell>
                <TableCell align="center"><strong>Rejected</strong></TableCell>
                <TableCell align="right"><strong>Total Amount</strong></TableCell>
                <TableCell align="right"><strong>Approved Amount</strong></TableCell>
                <TableCell align="right"><strong>Avg Amount</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {departmentStats.map((dept) => (
                <TableRow key={dept.departmentId} hover>
                  <TableCell>
                    <Typography variant="body1" fontWeight="medium">
                      {dept.departmentName}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip 
                      label={dept.totalRequests} 
                      color="primary" 
                      variant="outlined" 
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Chip 
                      label={dept.approvedRequests} 
                      color="success" 
                      variant="outlined" 
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Chip 
                      label={dept.pendingRequests} 
                      color="warning" 
                      variant="outlined" 
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Chip 
                      label={dept.rejectedRequests} 
                      color="error" 
                      variant="outlined" 
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight="medium">
                      {formatCurrency(dept.totalAmount)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" color="success.main" fontWeight="medium">
                      {formatCurrency(dept.approvedAmount)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" color="info.main">
                      {formatCurrency(dept.averageAmount)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Filtered Requests Details */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AssessmentIcon color="primary" />
          Filtered Requests Details
        </Typography>
        
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Showing {filteredRequests.length} request(s) with applied filters
        </Typography>

        <TableContainer sx={{ maxHeight: 600 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell><strong>ID</strong></TableCell>
                <TableCell><strong>Title</strong></TableCell>
                <TableCell><strong>Description</strong></TableCell>
                <TableCell><strong>Department</strong></TableCell>
                <TableCell><strong>Requester</strong></TableCell>
                <TableCell align="right"><strong>Amount</strong></TableCell>
                <TableCell align="center"><strong>Status</strong></TableCell>
                <TableCell><strong>Frequency</strong></TableCell>
                <TableCell><strong>Created</strong></TableCell>
                <TableCell><strong>Delivery Date</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRequests
                .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                .map((request) => (
                  <TableRow key={request.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium" color="primary.main">
                        #{request.id.slice(-8)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium" sx={{ maxWidth: 200 }}>
                        {request.title}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          maxWidth: 250,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                        title={request.description}
                      >
                        {request.description}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {request.department.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {request.requester.firstName} {request.requester.lastName}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="medium">
                        {formatCurrency(request.amount)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={request.status.replace(/_/g, ' ')}
                        color={getStatusColor(request.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {request.frequency || 'Once'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {format(new Date(request.createdAt), 'MMM dd, yyyy')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {request.requestedDeliveryDate 
                          ? format(new Date(request.requestedDeliveryDate), 'MMM dd, yyyy')
                          : 'N/A'
                        }
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>

        {filteredRequests.length === 0 && (
          <Box textAlign="center" py={4}>
            <Typography variant="body1" color="text.secondary">
              {requests.length === 0 
                ? "No requests found."
                : "No requests match your current filters."
              }
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Recent High-Value Requests */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MoneyIcon color="primary" />
                      Recent High-Value Requests (&gt;{formatCurrency(1000)})
        </Typography>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Title</strong></TableCell>
                <TableCell><strong>Department</strong></TableCell>
                <TableCell><strong>Requester</strong></TableCell>
                <TableCell align="right"><strong>Amount</strong></TableCell>
                <TableCell align="center"><strong>Status</strong></TableCell>
                <TableCell><strong>Created</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRequests
                .filter(request => request.amount > 1000)
                .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                .slice(0, 10)
                .map((request) => (
                  <TableRow key={request.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {request.title}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {request.department.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {request.requester.firstName} {request.requester.lastName}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="medium">
                        {formatCurrency(request.amount)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={request.status.replace(/_/g, ' ')}
                        color={getStatusColor(request.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {format(new Date(request.createdAt), 'MMM dd, yyyy')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>

        {filteredRequests.filter(request => request.amount > 1000).length === 0 && (
          <Box textAlign="center" py={4}>
            <Typography variant="body1" color="text.secondary">
              {requests.length === 0 
                ? "No requests found."
                : "No high-value requests match your current filters."
              }
            </Typography>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default GeneralManagerReports; 